import React from 'react';
import { MapPin, Phone, Mail, Linkedin, Twitter, Facebook } from 'lucide-react';
import { Link } from 'react-router-dom';
import { mockData } from './mock';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 text-gray-900 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-100/30 to-blue-100/30"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, rgba(59, 130, 246, 0.08) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(99, 102, 241, 0.08) 0%, transparent 50%)`,
          backgroundSize: '80px 80px'
        }}></div>
      </div>
      
      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 lg:gap-16">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link 
              to="/"
              className="flex items-center space-x-4 mb-8 hover:opacity-80 transition-opacity cursor-pointer"
            >
              <div className="flex-shrink-0">
                <img 
                  src={mockData.company.logo} 
                  alt="TSI Logo"
                  className="w-16 h-16 sm:w-20 sm:h-20 object-contain"
                  onError={(e) => {
                    // Fallback to initials if logo fails to load
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl sm:rounded-2xl items-center justify-center hidden">
                  <span className="text-white font-bold text-lg sm:text-2xl">TSI</span>
                </div>
              </div>
              <div>
                <h3 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-900 to-blue-800 bg-clip-text text-transparent">Traffic Service</h3>
                <p className="text-gray-600 font-medium text-sm sm:text-base">International</p>
              </div>
            </Link>
            <p className="text-gray-700 leading-relaxed mb-6 sm:mb-8 text-sm sm:text-base lg:text-lg">
              Leading provider of smart traffic technology and IoT solutions, 
              transforming cities across the Sultanate of Oman with innovative infrastructure.
            </p>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg sm:text-xl font-bold mb-6 sm:mb-8 text-gray-900 relative">
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Our Services</span>
              <div className="absolute -bottom-2 left-0 w-8 sm:w-12 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full"></div>
            </h4>
            <ul className="space-y-3 sm:space-y-4">
              <li>
                <Link 
                  to="/services"
                  className="group text-gray-700 hover:text-blue-600 transition-all duration-300 flex items-center space-x-2 sm:space-x-3 hover:translate-x-1 sm:hover:translate-x-2"
                >
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-400 rounded-full group-hover:bg-blue-600 group-hover:scale-125 transition-all duration-300 flex-shrink-0"></div>
                  <span className="font-medium text-sm sm:text-base">Smart Street Lighting</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/services"
                  className="group text-gray-700 hover:text-blue-600 transition-all duration-300 flex items-center space-x-2 sm:space-x-3 hover:translate-x-1 sm:hover:translate-x-2"
                >
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-400 rounded-full group-hover:bg-blue-600 group-hover:scale-125 transition-all duration-300 flex-shrink-0"></div>
                  <span className="font-medium text-sm sm:text-base">Traffic Enforcement</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/services"
                  className="group text-gray-700 hover:text-blue-600 transition-all duration-300 flex items-center space-x-2 sm:space-x-3 hover:translate-x-1 sm:hover:translate-x-2"
                >
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-400 rounded-full group-hover:bg-blue-600 group-hover:scale-125 transition-all duration-300 flex-shrink-0"></div>
                  <span className="font-medium text-sm sm:text-base">City Monitoring</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/services"
                  className="group text-gray-700 hover:text-blue-600 transition-all duration-300 flex items-center space-x-2 sm:space-x-3 hover:translate-x-1 sm:hover:translate-x-2"
                >
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-400 rounded-full group-hover:bg-blue-600 group-hover:scale-125 transition-all duration-300 flex-shrink-0"></div>
                  <span className="font-medium text-sm sm:text-base">Solar Solutions</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/services"
                  className="group text-gray-700 hover:text-blue-600 transition-all duration-300 flex items-center space-x-2 sm:space-x-3 hover:translate-x-1 sm:hover:translate-x-2"
                >
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-400 rounded-full group-hover:bg-blue-600 group-hover:scale-125 transition-all duration-300 flex-shrink-0"></div>
                  <span className="font-medium text-sm sm:text-base">Facade Lighting</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="text-lg sm:text-xl font-bold mb-6 sm:mb-8 text-gray-900 relative">
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Innovation</span>
              <div className="absolute -bottom-2 left-0 w-8 sm:w-12 h-1 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"></div>
            </h4>
            <ul className="space-y-3 sm:space-y-4">
              <li>
                <Link 
                  to="/solutions"
                  className="group text-gray-700 hover:text-indigo-600 transition-all duration-300 flex items-center space-x-2 sm:space-x-3 hover:translate-x-1 sm:hover:translate-x-2"
                >
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-indigo-400 rounded-full group-hover:bg-indigo-600 group-hover:scale-125 transition-all duration-300 flex-shrink-0"></div>
                  <span className="font-medium text-sm sm:text-base">Energy Saving Systems</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/solutions"
                  className="group text-gray-700 hover:text-indigo-600 transition-all duration-300 flex items-center space-x-2 sm:space-x-3 hover:translate-x-1 sm:hover:translate-x-2"
                >
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-indigo-400 rounded-full group-hover:bg-indigo-600 group-hover:scale-125 transition-all duration-300 flex-shrink-0"></div>
                  <span className="font-medium text-sm sm:text-base">Energy Storage</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/solutions"
                  className="group text-gray-700 hover:text-indigo-600 transition-all duration-300 flex items-center space-x-2 sm:space-x-3 hover:translate-x-1 sm:hover:translate-x-2"
                >
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-indigo-400 rounded-full group-hover:bg-indigo-600 group-hover:scale-125 transition-all duration-300 flex-shrink-0"></div>
                  <span className="font-medium text-sm sm:text-base">AI Traffic Intelligence</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/projects"
                  className="group text-gray-700 hover:text-indigo-600 transition-all duration-300 flex items-center space-x-2 sm:space-x-3 hover:translate-x-1 sm:hover:translate-x-2"
                >
                  <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-indigo-400 rounded-full group-hover:bg-indigo-600 group-hover:scale-125 transition-all duration-300 flex-shrink-0"></div>
                  <span className="font-medium text-sm sm:text-base">Our Projects</span>
                </Link>
              </li>
              {/* <li>
                <Link 
                  to="/career"
                  className="group text-gray-700 hover:text-indigo-600 transition-all duration-300 flex items-center space-x-3 hover:translate-x-2"
                >
                  <div className="w-2 h-2 bg-indigo-400 rounded-full group-hover:bg-indigo-600 group-hover:scale-125 transition-all duration-300"></div>
                  <span className="font-medium">Career</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/about"
                  className="group text-gray-700 hover:text-indigo-600 transition-all duration-300 flex items-center space-x-3 hover:translate-x-2"
                >
                  <div className="w-2 h-2 bg-indigo-400 rounded-full group-hover:bg-indigo-600 group-hover:scale-125 transition-all duration-300"></div>
                  <span className="font-medium">About Us</span>
                </Link>
              </li>
              <li>
                <Link 
                  to="/contact"
                  className="group text-gray-700 hover:text-indigo-600 transition-all duration-300 flex items-center space-x-3 hover:translate-x-2"
                >
                  <div className="w-2 h-2 bg-indigo-400 rounded-full group-hover:bg-indigo-600 group-hover:scale-125 transition-all duration-300"></div>
                  <span className="font-medium">Contact</span>
                </Link>
              </li> */}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg sm:text-xl font-bold mb-6 sm:mb-8 text-gray-900 relative">
              <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Contact Info</span>
              <div className="absolute -bottom-2 left-0 w-8 sm:w-12 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"></div>
            </h4>
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-start space-x-3 sm:space-x-4 group">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg flex-shrink-0">
                  <MapPin size={14} className="sm:w-4 sm:h-4 text-white" />
                </div>
                <div className="pt-1 sm:pt-2">
                  <p className="text-gray-700 font-medium text-sm sm:text-base">Traffic Service International</p>
                  <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                    Building No: 915, office No: 579<br />
                    CBD (Abu Dhabi bank) Al Falaj street,<br />
                    Muscat, Sultanate of Oman<br />
                    Postal Code: 111
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 sm:space-x-4 group">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg flex-shrink-0">
                  <Phone size={14} className="sm:w-4 sm:h-4 text-white" />
                </div>
                <div>
                  <p className="text-gray-700 font-medium text-sm sm:text-base">TEL: {mockData.contact.phone}</p>
                  <p className="text-gray-600 text-xs sm:text-sm">Mob: {mockData.contact.mobile}</p>
                </div>
              </div>
              <div className="flex items-center space-x-3 sm:space-x-4 group">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg sm:rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg flex-shrink-0">
                  <Mail size={14} className="sm:w-4 sm:h-4 text-white" />
                </div>
                <p className="text-gray-700 font-medium text-sm sm:text-base">{mockData.contact.email}</p>
              </div>
            </div>
            <div className="flex space-x-2 sm:space-x-3 mt-6 sm:mt-8">
              <a href="https://www.linkedin.com/company/traffic-service-international" target="_blank" rel="noopener noreferrer" className="group w-10 h-10 sm:w-12 sm:h-12 bg-white/80 backdrop-blur-sm rounded-lg sm:rounded-xl flex items-center justify-center hover:bg-[#0077B5] hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-[#0077B5]/25">
                <Linkedin size={18} className="sm:w-5 sm:h-5 text-gray-600 group-hover:text-white transition-colors" />
              </a>
              <a href={mockData.contact.social.twitter} className="group w-10 h-10 sm:w-12 sm:h-12 bg-white/80 backdrop-blur-sm rounded-lg sm:rounded-xl flex items-center justify-center hover:bg-[#1DA1F2] hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-[#1DA1F2]/25">
                <Twitter size={18} className="sm:w-5 sm:h-5 text-gray-600 group-hover:text-white transition-colors" />
              </a>
              <a href={mockData.contact.social.facebook} className="group w-10 h-10 sm:w-12 sm:h-12 bg-white/80 backdrop-blur-sm rounded-lg sm:rounded-xl flex items-center justify-center hover:bg-[#1877F2] hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-[#1877F2]/25">
                <Facebook size={18} className="sm:w-5 sm:h-5 text-gray-600 group-hover:text-white transition-colors" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-12 sm:mt-16 pt-6 sm:pt-8 border-t border-gray-400/60">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
            <p className="text-gray-600 text-xs sm:text-sm font-medium text-center sm:text-left">
              © {currentYear} Traffic Service International. All rights reserved.
            </p>
            <div className="flex space-x-4 sm:space-x-6 lg:space-x-8 text-xs sm:text-sm">
              <Link to="/privacy-policy" className="text-gray-600 hover:text-blue-600 transition-colors duration-300 font-medium hover:underline">
                Privacy Policy
              </Link>
              <Link to="/terms-of-service" className="text-gray-600 hover:text-blue-600 transition-colors duration-300 font-medium hover:underline">
                Terms of Service
              </Link>
              <Link to="/cookie-policy" className="text-gray-600 hover:text-blue-600 transition-colors duration-300 font-medium hover:underline">
                Cookie Policy
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
